import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Linking,
  Alert,
  TouchableOpacity,
} from 'react-native';
import {
  Card,
  Title,
  Paragraph,
  Chip,
  Button,
  Divider,
  List,
  Badge,
  useTheme,
  Text,
} from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import apiService, { Distillery } from '../services/ApiService';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { ThemedContainer } from '../components/ThemedContainer';

interface RumDetailScreenProps {
  route: {
    params: {
      rum: any;
    };
  };
  navigation: any;
}

const RumDetailScreen: React.FC<RumDetailScreenProps> = ({ route, navigation }) => {
  const { rum } = route.params;
  const theme = useTheme();
  const { themeData } = useCustomTheme();
  const [showMissingData, setShowMissingData] = useState(false);

  // Safety check for undefined rum
  if (!rum) {
    return (
      <ThemedContainer style={styles.container}>
        <Text style={[styles.errorText, { color: themeData.colors.text }]}>
          Rum not found
        </Text>
      </ThemedContainer>
    );
  }

  // Set up navigation header buttons
  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <TouchableOpacity
          style={styles.headerButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={themeData.colors.primary} />
        </TouchableOpacity>
      ),
      headerRight: () => (
        <View style={styles.headerRight}>
          <TouchableOpacity
            style={styles.headerButton}
            onPress={async () => {
              // Navigate to distillery detail if available
              if (rum.distillery_id) {
                try {
                  navigation.navigate('DistilleryDetail', { distilleryId: rum.distillery_id });
                } catch (error) {
                  Alert.alert('Error', 'Could not navigate to distillery details');
                }
              } else {
                Alert.alert('Distillery Not Found', `No detailed information available for this rum`);
              }
            }}
          >
            <Ionicons name="business" size={24} color={themeData.colors.primary} />
          </TouchableOpacity>
        </View>
      ),
    });
  }, [navigation, rum.distillery]);

  const openTotalWine = () => {
    // First, check if we have a direct product URL
    if (rum.source_url && rum.source_url.includes('totalwine.com')) {
      // Validate it's a product page, not just a search
      if (rum.source_url.includes('/p/') || rum.source_url.includes('/product/')) {
        Linking.openURL(rum.source_url);
        return;
      }
    }
    
    // Fall back to intelligent search
    const searchQuery = generateTotalWineSearchQuery(rum);
    const url = `https://www.totalwine.com/search/all?text=${encodeURIComponent(searchQuery)}`;
    Linking.openURL(url);
  };

  const generateTotalWineSearchQuery = (rum: any) => {
    // Create a more targeted search query
    let searchTerms = [];
    
    // Start with the rum name
    if (rum.name) {
      searchTerms.push(rum.name);
    }
    
    // Add distillery if available
    if (rum.distillery && rum.distillery !== 'Unknown') {
      searchTerms.push(rum.distillery);
    }
    
    // Add age statement if available
    if (rum.age && rum.age !== 'Unknown') {
      searchTerms.push(rum.age);
    }
    
    // Add proof/ABV if available
    if (rum.abv) {
      searchTerms.push(`${rum.abv}%`);
    }
    
    // Combine terms and clean up
    let query = searchTerms.join(' ');
    
    // Remove diacritics and special characters
    query = query.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    
    // Clean up extra spaces
    query = query.replace(/\s+/g, ' ').trim();
    
    return query;
  };

  const openDistilleryLink = () => {
    // Navigate to distillery detail if available
    if (rum.distillery_id) {
      navigation.navigate('DistilleryDetail', { distilleryId: rum.distillery_id });
    } else {
      Alert.alert('Distillery Not Found', `No detailed information available for this rum`);
    }
  };

  const getStyleTags = () => {
    const tags = [];
    if (rum.abv && parseFloat(rum.abv) >= 57) tags.push('Overproof');
    if (rum.age && rum.age !== 'Unknown') tags.push('Aged');
    if (rum.type) tags.push(rum.type);
    return tags;
  };

  const getTransparencyBadges = () => {
    const badges = [];
    // Add logic for transparency badges based on data
    if (rum.additives === 'none') badges.push('No Additives');
    if (rum.country === 'Martinique') badges.push('AOC');
    if (rum.awards && rum.awards.length > 0) badges.push('Awarded');
    return badges;
  };

  const renderSection = (title: string, children: React.ReactNode, hasData: boolean = true) => {
    if (!hasData && !showMissingData) return null;
    
    return (
      <Card style={[styles.sectionCard, { backgroundColor: themeData.colors.cardBackground }]} mode="outlined">
        <Card.Content>
          <Title style={[styles.sectionTitle, { color: themeData.colors.text }]}>{title}</Title>
          {children}
        </Card.Content>
      </Card>
    );
  };

  const hasValue = (value: any) => {
    return value && value !== 'Unknown' && value !== 'Coming soon' && value !== 'Description coming soon...';
  };

  return (
    <ThemedContainer style={styles.container}>
      <ScrollView style={[styles.scrollView, { backgroundColor: themeData.colors.background }]}>
        {/* Top Card - Hero Section */}
        <Card style={[styles.heroCard, { backgroundColor: themeData.colors.cardBackground }]}>
          <Card.Content>
            <View style={styles.heroHeader}>
              <View style={styles.heroText}>
                <Title style={[styles.rumName, { color: themeData.colors.text }]}>{rum.name}</Title>
                <Text style={[styles.brandText, { color: themeData.colors.textSecondary }]}>{rum.distillery}</Text>
                <Text style={[styles.locationText, { color: themeData.colors.textSecondary }]}>
                  {rum.country} {rum.region && `• ${rum.region}`}
                </Text>
              </View>
              <View style={styles.heroStats}>
                <Text style={[styles.abvText, { color: themeData.colors.highlight1 }]}>{rum.abv || '40%'}</Text>
                <Text style={[styles.sizeText, { color: themeData.colors.textSecondary }]}>750ml</Text>
              </View>
            </View>

            <View style={styles.styleTags}>
              {getStyleTags().map((tag, index) => (
                <Chip key={index} style={[styles.tag, { backgroundColor: themeData.colors.highlight2 + '20' }]} mode="outlined">
                  <Text style={{ color: themeData.colors.highlight2 }}>{tag}</Text>
                </Chip>
              ))}
            </View>

            <View style={styles.transparencyBadges}>
              {getTransparencyBadges().map((badge, index) => (
                <Badge key={index} style={[styles.badge, { backgroundColor: themeData.colors.highlight3 }]}>
                  {badge}
                </Badge>
              ))}
            </View>

             <View style={styles.actionButtons}>
               <Button
                 mode="contained"
                 onPress={openTotalWine}
                 style={[styles.actionButton, { backgroundColor: themeData.colors.highlight1 }]}
                 icon="shopping"
               >
                 Buy on Total Wine
               </Button>
               <Button
                 mode="outlined"
                 onPress={openDistilleryLink}
                 style={[styles.actionButton, { borderColor: themeData.colors.highlight2 }]}
                 icon="factory"
                 textColor={themeData.colors.highlight2}
               >
                 Distillery Details
               </Button>
               <Button
                 mode="outlined"
                 onPress={() => navigation.goBack()}
                 style={[styles.actionButton, { borderColor: themeData.colors.textSecondary }]}
                 icon="arrow-left"
                 textColor={themeData.colors.textSecondary}
               >
                 Back to Rums
               </Button>
             </View>

            <View style={styles.missingDataToggle}>
              <Button
                mode="text"
                onPress={() => setShowMissingData(!showMissingData)}
                icon={showMissingData ? "eye-off" : "eye"}
                style={styles.toggleButton}
                textColor={themeData.colors.textSecondary}
              >
                {showMissingData ? "Hide Missing Data" : "Show Missing Data"}
              </Button>
            </View>
          </Card.Content>
        </Card>

        {/* Description - Moved to top */}
        {hasValue(rum.description) && (
          <Card style={[styles.sectionCard, { backgroundColor: themeData.colors.cardBackgroundVariant }]} mode="outlined">
            <Card.Content>
              <Title style={[styles.sectionTitle, { color: themeData.colors.text }]}>Description</Title>
              <Paragraph style={{ color: themeData.colors.textSecondary }}>{rum.description}</Paragraph>
            </Card.Content>
          </Card>
        )}

        {/* Provenance Section */}
        {renderSection('Provenance', (
          <View>
            {hasValue(rum.vintage_year) && (
              <List.Item
                title="Distilled"
                description={rum.vintage_year}
                left={props => <List.Icon {...props} icon="calendar" color={themeData.colors.highlight1} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
            {hasValue(rum.bottled_year) && (
              <List.Item
                title="Bottled"
                description={rum.bottled_year}
                left={props => <List.Icon {...props} icon="bottle-wine" color={themeData.colors.highlight2} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
            {hasValue(rum.release_type) && (
              <List.Item
                title="Release Type"
                description={rum.release_type}
                left={props => <List.Icon {...props} icon="tag" color={themeData.colors.highlight3} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
            {hasValue(rum.batch_id) && (
              <List.Item
                title="Batch/Cask ID"
                description={rum.batch_id}
                left={props => <List.Icon {...props} icon="identifier" color={themeData.colors.highlight4} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
          </View>
        ), hasValue(rum.vintage_year) || hasValue(rum.bottled_year) || hasValue(rum.release_type) || hasValue(rum.batch_id))}

        {/* Production Section */}
        {renderSection('Production', (
          <View>
            {hasValue(rum.base_material) && (
              <List.Item
                title="Base Material"
                description={rum.base_material}
                left={props => <List.Icon {...props} icon="leaf" color={themeData.colors.highlight1} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
            {hasValue(rum.still_type) && (
              <List.Item
                title="Still Type"
                description={rum.still_type}
                left={props => <List.Icon {...props} icon="cog" color={themeData.colors.highlight2} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
            {hasValue(rum.fermentation_time) && (
              <List.Item
                title="Fermentation"
                description={rum.fermentation_time}
                left={props => <List.Icon {...props} icon="clock" color={themeData.colors.highlight3} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
            {hasValue(rum.yeast_type) && (
              <List.Item
                title="Yeast"
                description={rum.yeast_type}
                left={props => <List.Icon {...props} icon="microscope" color={themeData.colors.highlight4} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
          </View>
        ), hasValue(rum.base_material) || hasValue(rum.still_type) || hasValue(rum.fermentation_time) || hasValue(rum.yeast_type))}

        {/* Maturation Section */}
        {renderSection('Maturation', (
          <View>
            {hasValue(rum.age) && (
              <List.Item
                title="Age Statement"
                description={rum.age}
                left={props => <List.Icon {...props} icon="timer" color={themeData.colors.highlight1} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
            {hasValue(rum.cask_types) && (
              <List.Item
                title="Cask Types"
                description={rum.cask_types}
                left={props => <List.Icon {...props} icon="barrel" color={themeData.colors.highlight2} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
            {hasValue(rum.finish_types) && (
              <List.Item
                title="Finishes"
                description={rum.finish_types}
                left={props => <List.Icon {...props} icon="layers" color={themeData.colors.highlight3} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
            {hasValue(rum.tropical_aging_pct) && (
              <List.Item
                title="Tropical Aging"
                description={`${rum.tropical_aging_pct}%`}
                left={props => <List.Icon {...props} icon="thermometer" color={themeData.colors.highlight4} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
          </View>
        ), hasValue(rum.age) || hasValue(rum.cask_types) || hasValue(rum.finish_types) || hasValue(rum.tropical_aging_pct))}

        {/* Composition & Transparency Section */}
        {renderSection('Composition & Transparency', (
          <View>
            {hasValue(rum.additives_flag) && (
              <List.Item
                title="Additives"
                description={rum.additives_flag}
                left={props => <List.Icon {...props} icon="plus-circle" color={themeData.colors.highlight1} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
            {hasValue(rum.color_added_flag) && (
              <List.Item
                title="Color Added"
                description={rum.color_added_flag}
                left={props => <List.Icon {...props} icon="palette" color={themeData.colors.highlight2} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
            {hasValue(rum.chill_filtered_flag) && (
              <List.Item
                title="Chill Filtered"
                description={rum.chill_filtered_flag}
                left={props => <List.Icon {...props} icon="snowflake" color={themeData.colors.highlight3} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
            {hasValue(rum.ester_level) && (
              <List.Item
                title="Ester Level"
                description={rum.ester_level}
                left={props => <List.Icon {...props} icon="chart-line" color={themeData.colors.highlight4} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
          </View>
        ), hasValue(rum.additives_flag) || hasValue(rum.color_added_flag) || hasValue(rum.chill_filtered_flag) || hasValue(rum.ester_level))}

        {/* Sensory Section */}
        {renderSection('Sensory Profile', (
          <View>
            {hasValue(rum.tasting_notes) && (
              <View style={styles.tastingNotes}>
                <Text style={[styles.tastingTitle, { color: themeData.colors.text }]}>Tasting Notes</Text>
                <Paragraph style={{ color: themeData.colors.textSecondary }}>{rum.tasting_notes}</Paragraph>
              </View>
            )}
            <View style={styles.flavorTags}>
              <Text style={[styles.flavorTitle, { color: themeData.colors.text }]}>Flavor Profile</Text>
              <View style={styles.tagRow}>
                {['Vanilla', 'Oak', 'Spice', 'Fruit'].map((flavor, index) => (
                  <Chip key={index} style={[styles.flavorTag, { backgroundColor: themeData.colors.highlight1 + '20' }]} mode="outlined">
                    <Text style={{ color: themeData.colors.highlight1 }}>{flavor}</Text>
                  </Chip>
                ))}
              </View>
            </View>
            <List.Item
              title="Best Served"
              description="Neat or in cocktails"
              left={props => <List.Icon {...props} icon="glass-cocktail" color={themeData.colors.highlight2} />}
              titleStyle={{ color: themeData.colors.text }}
              descriptionStyle={{ color: themeData.colors.textSecondary }}
            />
          </View>
        ), hasValue(rum.tasting_notes))}

        {/* Release & Commerce Section */}
        {renderSection('Release & Commerce', (
          <View>
            {hasValue(rum.price_range) && (
              <List.Item
                title="Price Range"
                description={rum.price_range}
                left={props => <List.Icon {...props} icon="currency-usd" color={themeData.colors.highlight3} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
            <List.Item
              title="Bottle Size"
              description="750ml"
              left={props => <List.Icon {...props} icon="ruler" color={themeData.colors.highlight4} />}
              titleStyle={{ color: themeData.colors.text }}
              descriptionStyle={{ color: themeData.colors.textSecondary }}
            />
            {hasValue(rum.bottle_count) && (
              <List.Item
                title="Limited Release"
                description={`${rum.bottle_count} bottles`}
                left={props => <List.Icon {...props} icon="numeric" color={themeData.colors.highlight1} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
            {hasValue(rum.availability_note) && (
              <List.Item
                title="Availability"
                description={rum.availability_note}
                left={props => <List.Icon {...props} icon="earth" color={themeData.colors.highlight2} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
          </View>
        ), hasValue(rum.price_range) || hasValue(rum.bottle_count) || hasValue(rum.availability_note))}

        {/* Recognition Section */}
        {renderSection('Recognition', (
          <View>
            {hasValue(rum.rating) && (
              <List.Item
                title="Rating"
                description={rum.rating}
                left={props => <List.Icon {...props} icon="star" color={themeData.colors.highlight3} />}
                titleStyle={{ color: themeData.colors.text }}
                descriptionStyle={{ color: themeData.colors.textSecondary }}
              />
            )}
            {rum.awards && rum.awards.length > 0 && (
              <View style={styles.awardsSection}>
                <Text style={[styles.awardsTitle, { color: themeData.colors.text }]}>Awards</Text>
                {rum.awards.map((award: string, index: number) => (
                  <Chip key={index} style={[styles.awardChip, { backgroundColor: themeData.colors.highlight4 + '20' }]} mode="outlined">
                    <Text style={{ color: themeData.colors.highlight4 }}>{award}</Text>
                  </Chip>
                ))}
              </View>
            )}
          </View>
        ), hasValue(rum.rating) || (rum.awards && rum.awards.length > 0))}

        {/* Links Section */}
        {renderSection('Links & Resources', (
          <View>
            <Button
              mode="outlined"
              onPress={openTotalWine}
              style={[styles.linkButton, { borderColor: themeData.colors.highlight1 }]}
              icon="shopping"
              textColor={themeData.colors.highlight1}
            >
              Total Wine
            </Button>
            <Button
              mode="outlined"
              onPress={() => {}}
              style={[styles.linkButton, { borderColor: themeData.colors.highlight2 }]}
              icon="file-document"
              textColor={themeData.colors.highlight2}
            >
              Fact Sheet
            </Button>
            <Button
              mode="outlined"
              onPress={() => {}}
              style={[styles.linkButton, { borderColor: themeData.colors.highlight3 }]}
              icon="web"
              textColor={themeData.colors.highlight3}
            >
              Producer Website
            </Button>
          </View>
        ), true)}
             </ScrollView>

       {/* Custom Bottom Navigation */}
       <View style={[styles.bottomNav, { backgroundColor: themeData.colors.surface, borderTopColor: themeData.colors.border }]}>
         <TouchableOpacity 
           style={styles.navItem} 
           onPress={() => navigation.navigate('Home' as never)}
         >
           <Ionicons name="home-outline" size={24} color={themeData.colors.textSecondary} />
           <Text style={[styles.navText, { color: themeData.colors.textSecondary }]}>Home</Text>
         </TouchableOpacity>
         <TouchableOpacity 
           style={styles.navItem} 
           onPress={() => navigation.navigate('Tiki Bars' as never)}
         >
           <Ionicons name="beer-outline" size={24} color={themeData.colors.textSecondary} />
           <Text style={[styles.navText, { color: themeData.colors.textSecondary }]}>Bars</Text>
         </TouchableOpacity>
         <TouchableOpacity 
           style={styles.navItem} 
           onPress={() => navigation.navigate('Cocktails' as never)}
         >
           <Ionicons name="wine-outline" size={24} color={themeData.colors.textSecondary} />
           <Text style={[styles.navText, { color: themeData.colors.textSecondary }]}>Cocktails</Text>
         </TouchableOpacity>
         <TouchableOpacity 
           style={styles.navItem} 
           onPress={() => navigation.navigate('Rums' as never)}
         >
           <Ionicons name="flask-outline" size={24} color={themeData.colors.primary} />
           <Text style={[styles.navText, { color: themeData.colors.primary }]}>Rums</Text>
         </TouchableOpacity>
         <TouchableOpacity 
           style={styles.navItem} 
           onPress={() => navigation.navigate('Distilleries' as never)}
         >
           <Ionicons name="business-outline" size={24} color={themeData.colors.textSecondary} />
           <Text style={[styles.navText, { color: themeData.colors.textSecondary }]}>Distilleries</Text>
         </TouchableOpacity>
         <TouchableOpacity 
           style={styles.navItem} 
           onPress={() => navigation.navigate('Culture' as never)}
         >
           <Ionicons name="library-outline" size={24} color={themeData.colors.textSecondary} />
           <Text style={[styles.navText, { color: themeData.colors.textSecondary }]}>Culture</Text>
         </TouchableOpacity>
         <TouchableOpacity 
           style={styles.navItem} 
           onPress={() => navigation.navigate('Blog' as never)}
         >
           <Ionicons name="newspaper-outline" size={24} color={themeData.colors.textSecondary} />
           <Text style={[styles.navText, { color: themeData.colors.textSecondary }]}>Blog</Text>
         </TouchableOpacity>
       </View>
     </ThemedContainer>
   );
 };

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  heroCard: {
    margin: 16,
    elevation: 4,
  },
  heroHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  heroText: {
    flex: 1,
  },
  rumName: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  brandText: {
    fontSize: 16,
    marginBottom: 2,
  },
  locationText: {
    fontSize: 14,
  },
  heroStats: {
    alignItems: 'flex-end',
  },
  abvText: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  sizeText: {
    fontSize: 14,
  },
  styleTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  tag: {
    marginRight: 8,
    marginBottom: 8,
  },
  transparencyBadges: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  badge: {
    marginRight: 8,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  actionButton: {
    flex: 1,
  },
  sectionCard: {
    margin: 16,
    marginTop: 8,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  tastingNotes: {
    marginBottom: 16,
  },
  tastingTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  flavorTags: {
    marginBottom: 16,
  },
  flavorTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  tagRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  flavorTag: {
    marginRight: 8,
    marginBottom: 8,
  },
  awardsSection: {
    marginBottom: 16,
  },
  awardsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  awardChip: {
    marginRight: 8,
    marginBottom: 8,
  },
  linkButton: {
    marginBottom: 8,
  },
  missingDataToggle: {
    marginTop: 16,
    alignItems: 'center',
  },
  toggleButton: {
    marginTop: 8,
  },
  headerButton: {
    padding: 8,
    marginHorizontal: 4,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
     errorText: {
     fontSize: 18,
     fontWeight: 'bold',
     textAlign: 'center',
     marginTop: 20,
   },
   bottomNav: {
     flexDirection: 'row',
     justifyContent: 'space-around',
     paddingVertical: 10,
     position: 'absolute',
     bottom: 0,
     left: 0,
     right: 0,
     zIndex: 10,
     borderTopWidth: 1,
   },
   navItem: {
     alignItems: 'center',
   },
   navText: {
     fontSize: 12,
     marginTop: 4,
   },
   
 });

export default RumDetailScreen;
